import { useEffect, useState, useSyncExternalStore } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthOptional } from '../../../contexts/auth-context'
import { getCachedDirectoryUser, listDirectoryUsers } from '../../../services/chat-service'
import { fetchActiveCallById, fetchActiveCallSessionUuid } from '../../../services/voice-call-service'
import { keepAliveAuthSession } from '../../../services/auth-service'
import { onConsentWindowEnded } from '../../../utils/consent-window-end'
import {
  getVoiceCallSession,
  patchVoiceCallSession,
  subscribeVoiceCallSession,
} from '../../../stores/voice-call-store'
import type { VoiceCallSession } from '../../../types/voice-call'
import { isIndependentUser, isProvider } from '../../../utils/rbac'
import { getSessionsRepository } from '../data/sessions-repository-provider'
import {
  createSessionCallRecorder,
  type SessionCallNotice,
  type SessionCallRecorder,
} from '../utils/session-call-recorder'
import { couldBeMemberSessionCall } from '../utils/resolve-session-call'
import { publishSessionLifecycleResult } from '../utils/session-lifecycle-events'

function newEventId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  return `evt-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

function safeLocalStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null
  } catch {
    return null
  }
}

/**
 * Natively answered ring (no INVITE payload in JS): look the session up on the
 * server only when the caller is a member with a telehealth session in its
 * call window — the lookup is not free, and most calls are not session calls.
 */
async function resolveNativelyAnsweredSessionCall(
  callId: string,
  peerUserId: string | undefined,
): Promise<string | null> {
  if (!peerUserId) return null
  const peer =
    getCachedDirectoryUser(peerUserId) ?? (await listDirectoryUsers()).find((u) => u.userId === peerUserId)
  const sessions = await getSessionsRepository().listMySessions()
  if (!couldBeMemberSessionCall(sessions, peer?.email, Date.now())) return null
  return fetchActiveCallSessionUuid(callId)
}

function toSnapshot(call: VoiceCallSession | null) {
  return call ? { ...call, peerUserId: call.peer?.userId } : null
}

const NO_NOTICE = () => null
const NO_SUBSCRIBE = () => () => {}

/** Copy for the call-recorded start/end snackbar. */
export function sessionCallNoticeText(notice: SessionCallNotice): string {
  // Never "session started" in provider copy (AHP owns the official start).
  switch (notice.kind) {
    case 'started':
      return 'Start time recorded — call connected'
    case 'ended':
      return 'End time recorded — call ended'
    case 'manual':
    case 'info':
    case 'error':
      return notice.message
  }
}

/**
 * Top-anchored (the in-call controls own the bottom of the screen) and above
 * the call overlays (z-60…66) so the Undo is reachable mid-call.
 */
export function SessionCallNoticeSnackbar({
  notice,
  onUndo,
  onDismiss,
  onOpenSession,
}: {
  notice: SessionCallNotice | null
  onUndo: () => void
  onDismiss: () => void
  /** Record-manually prompt: open the session's Attendance card (manual buttons). */
  onOpenSession?: (sessionUuid: string) => void
}) {
  if (!notice) return null
  if (notice.kind === 'manual') {
    return (
      <div
        className="pointer-events-none fixed inset-x-0 top-[calc(max(0.5rem,var(--safe-top))+3.25rem)] z-[67] flex justify-center px-4"
        role="alert"
        aria-live="assertive"
      >
        <div className="pointer-events-auto flex max-w-md flex-col gap-2 rounded-2xl bg-amber-50 p-4 text-amber-950 shadow-xl ring-1 ring-amber-300">
          <p className="text-sm font-semibold">{notice.message}</p>
          <div className="flex flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={onDismiss}
              className="a11y-touch-target rounded-lg px-3 py-2 text-sm font-semibold text-amber-900 hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
            >
              Not now
            </button>
            <button
              type="button"
              onClick={() => {
                onOpenSession?.(notice.sessionUuid)
                onDismiss()
              }}
              className="a11y-touch-target rounded-lg bg-amber-600 px-3 py-2 text-sm font-semibold text-white hover:bg-amber-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2"
            >
              Record session times
            </button>
          </div>
        </div>
      </div>
    )
  }
  const undoable = notice.kind === 'started' || notice.kind === 'ended'
  const isError = notice.kind === 'error'
  return (
    <div
      className="pointer-events-none fixed inset-x-0 top-[calc(max(0.5rem,var(--safe-top))+3.25rem)] z-[67] flex justify-center px-4"
      role={isError ? 'alert' : 'status'}
      aria-live={isError ? 'assertive' : 'polite'}
    >
      <div
        className={`pointer-events-auto flex max-w-full items-center gap-3 rounded-full py-2 pl-5 pr-2 text-white shadow-xl ${
          isError ? 'bg-red-700' : 'bg-slate-900'
        }`}
      >
        <span className="text-sm font-semibold">{sessionCallNoticeText(notice)}</span>
        {undoable ? (
          <button
            type="button"
            onClick={onUndo}
            disabled={notice.undoing}
            aria-label={notice.kind === 'started' ? 'Undo the recorded start time' : 'Undo the recorded end time'}
            className="a11y-touch-target rounded-full px-4 py-2 text-sm font-bold uppercase tracking-wide text-amber-300 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 disabled:opacity-50"
          >
            {notice.undoing ? 'Undoing…' : 'Undo'}
          </button>
        ) : (
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss"
            className="a11y-touch-target rounded-full px-3 py-2 text-sm font-semibold text-white/80 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            OK
          </button>
        )}
      </div>
    </div>
  )
}

/** A natively answered / redialled call turned out to be the session call. */
function markLiveCallAsSession(callId: string, sessionUuid: string): void {
  const live = getVoiceCallSession()
  // Chip + no Transfer / Add person on the overlay, like a labelled call.
  if (live?.callId === callId && !live.sessionUuid) patchVoiceCallSession({ sessionUuid })
}

function SessionCallRecorderRunner({ userId }: { userId: string }) {
  const [recorder, setRecorder] = useState<SessionCallRecorder | null>(null)
  const navigate = useNavigate()

  useEffect(() => {
    const next = createSessionCallRecorder({
      userId,
      submit: (event) => getSessionsRepository().submitLifecycleEvent(event),
      refreshAuth: keepAliveAuthSession,
      onSessionResolved: markLiveCallAsSession,
      resolveSessionUuid: resolveNativelyAnsweredSessionCall,
      isCallLive: async (callId) => (await fetchActiveCallById(callId)) != null,
      onResult: publishSessionLifecycleResult,
      newEventId,
      storage: safeLocalStorage(),
    })
    setRecorder(next)
    next.restorePending()
    next.observe(toSnapshot(getVoiceCallSession()))
    const unsubscribe = subscribeVoiceCallSession(() => next.observe(toSnapshot(getVoiceCallSession())))
    const offConsentEnd = onConsentWindowEnded((callId) => next.reclassifyAsConsentEnd(callId))
    // Pending ends that failed to send retry the moment the app is back.
    const retry = () => next.retryPendingNow()
    const onVisibility = () => {
      if (document.visibilityState === 'visible') retry()
    }
    window.addEventListener('online', retry)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      unsubscribe()
      offConsentEnd()
      window.removeEventListener('online', retry)
      document.removeEventListener('visibilitychange', onVisibility)
      next.dispose()
      setRecorder(null)
    }
  }, [userId])

  const notice = useSyncExternalStore(
    recorder?.subscribe ?? NO_SUBSCRIBE,
    recorder?.getNotice ?? NO_NOTICE,
  )

  return (
    <SessionCallNoticeSnackbar
      notice={notice}
      onUndo={() => void recorder?.undo()}
      onDismiss={() => recorder?.dismissNotice()}
      onOpenSession={(uuid) => navigate(`/sessions?session=${encodeURIComponent(uuid)}`)}
    />
  )
}

/**
 * App-wide (authenticated shell, outside the soft-lock gate like the other
 * call surfaces) recorder that turns a telehealth session call into the
 * session's start/end on the PROVIDER's device. Members and followers never
 * write session times. Keyed by user so an account switch starts clean.
 */
export function SessionCallRecorderController() {
  const auth = useAuthOptional()
  const user = auth && !auth.isLoading ? auth.user : null
  if (!user || !isProvider(user) || isIndependentUser(user)) return null
  return <SessionCallRecorderRunner key={user.userId} userId={user.userId} />
}
